/**
 * Navigation between search results ("inventory"), a vehicle, and My Garage.
 *
 * The results context is what lets a vehicle page say "Back to results" and offer
 * previous and next vehicles. It is kept in sessionStorage, so it belongs to one browser tab and
 * is lost when the tab closes. Every read and write is guarded: a blocked or full storage
 * means the context is missing, and the pages fall back to their plain routes.
 *
 * Only search-results paths can be returned to. An unknown or hostile value falls back
 * to the inventory, which is always a valid page.
 */
import { sanitizeReturnPath } from './safeReturnPath';

export const INVENTORY_PATH = '/s';
export const GARAGE_PATH = '/manage-listings';

const CONTEXT_KEY = 'skipthedealerships.inventoryContext';

// `/s` and `/s/<listing type>`, with an optional query string. Nothing else.
const INVENTORY_PATH_PATTERN = /^\/s(\/[^/?#]+)?(\?[^#]*)?$/;

const storage = () => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

const readItem = key => {
  try {
    const store = storage();
    return store ? store.getItem(key) : null;
  } catch (e) {
    return null;
  }
};

const writeItem = (key, value) => {
  try {
    const store = storage();
    if (store) store.setItem(key, value);
  } catch (e) {
    // Storage can be full or disabled. The pages still work without it.
  }
};

/**
 * Returns the path only if it is a search-results path, otherwise null.
 * @param {string} value
 * @returns {string|null}
 */
export const safeInventoryPath = value => {
  const safe = sanitizeReturnPath(value, null);
  return safe && INVENTORY_PATH_PATTERN.test(safe) ? safe : null;
};

/** The inventory path to use when there is no usable results context. */
export const inventoryFallbackPath = () => INVENTORY_PATH;

const isListingId = value => typeof value === 'string' && /^[0-9a-f-]{36}$/i.test(value);

/**
 * @typedef {Object} InventoryItem
 * @property {string} id      listing UUID
 * @property {string} slug    URL slug used by the listing route
 */

/**
 * Saves the results on the current search page.
 * @param {Object} context
 * @param {string} context.returnTo  path and query of the results page
 * @param {InventoryItem[]} context.items  results in display order
 * @param {number} [context.scrollY]
 */
export const saveInventoryContext = ({ returnTo, items, scrollY }) => {
  const safe = safeInventoryPath(returnTo);
  if (!safe || !Array.isArray(items)) return;

  const cleanItems = items
    .filter(item => item && isListingId(item.id) && typeof item.slug === 'string')
    .map(item => ({ id: item.id, slug: item.slug }));

  const previous = loadInventoryContext();
  // Keep the saved scroll position when the same results are refreshed.
  const keepScroll = previous && previous.returnTo === safe;
  writeItem(
    CONTEXT_KEY,
    JSON.stringify({
      returnTo: safe,
      items: cleanItems,
      scrollY: Number.isFinite(scrollY) ? scrollY : keepScroll ? previous.scrollY : 0,
    })
  );
};

/**
 * @returns {{returnTo: string, items: InventoryItem[], scrollY: number}|null}
 */
export const loadInventoryContext = () => {
  const raw = readItem(CONTEXT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const returnTo = safeInventoryPath(parsed && parsed.returnTo);
    if (!returnTo || !Array.isArray(parsed.items)) return null;
    const scrollY = Number.isFinite(parsed.scrollY) && parsed.scrollY >= 0 ? parsed.scrollY : 0;
    return { returnTo, items: parsed.items.filter(i => i && isListingId(i.id)), scrollY };
  } catch (e) {
    return null;
  }
};

/** Records the scroll position for the results page the context belongs to. */
export const saveInventoryScroll = (returnTo, scrollY) => {
  const context = loadInventoryContext();
  if (!context || context.returnTo !== safeInventoryPath(returnTo)) return;
  if (!Number.isFinite(scrollY) || scrollY < 0) return;
  writeItem(CONTEXT_KEY, JSON.stringify({ ...context, scrollY }));
};

/** The path to return to from a vehicle page: the saved results, or the inventory. */
export const inventoryBackPath = () => {
  const context = loadInventoryContext();
  return context ? context.returnTo : inventoryFallbackPath();
};

/**
 * Previous and next vehicle in the saved results, or null when the vehicle is not part of them.
 * @param {string} listingId
 * @returns {{prev: InventoryItem|null, next: InventoryItem|null, position: number, total: number}|null}
 */
export const neighbourVehicles = listingId => {
  const context = loadInventoryContext();
  if (!context) return null;
  const index = context.items.findIndex(i => i.id === listingId);
  if (index === -1) return null;
  return {
    prev: index > 0 ? context.items[index - 1] : null,
    next: index < context.items.length - 1 ? context.items[index + 1] : null,
    position: index + 1,
    total: context.items.length,
  };
};

/**
 * Where the edit page returns to. The only way into the editor is from My Garage, and a direct
 * link has no record of a previous page, so both cases return to My Garage. The editor is only
 * for the seller's own listings, so My Garage is always a valid place to land.
 */
export const editReturnPath = () => GARAGE_PATH;
